import { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  publicI18n,
  dashboardI18n,
  setPublicLocale,
  setDashboardLocale,
  DEFAULT_LOCALE,
  localeMeta,
} from "./index";

const DASHBOARD_PREFIXES = ["/admin", "/dashboard", "/perfil", "/causas/trocar", "/completar-perfil"];
const LS_DASHBOARD_LOCALE = "dashboard_locale";
const LS_PUBLIC_LOCALE = "public_locale";

function isDashboardRoute(pathname: string) {
  return DASHBOARD_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

/** Locale conhecido localmente — aplicado de forma síncrona, sem esperar rede. */
function readCachedPublicLocale(): string {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  return localStorage.getItem(LS_PUBLIC_LOCALE) || DEFAULT_LOCALE;
}

/**
 * Sincroniza o locale público global (`countries.is_default`) SEM bloquear o
 * primeiro paint: começa com o valor em cache local e só consulta o banco
 * quando o navegador estiver ocioso, fora da janela do LCP.
 */
function usePublicLocaleSync() {
  const [cached, setCached] = useState<string>(readCachedPublicLocale);
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    setPublicLocale(cached);
  }, [cached]);

  useEffect(() => {
    const schedule = () => setIdle(true);
    const ric = (window as any).requestIdleCallback;
    if (ric) {
      const id = ric(schedule, { timeout: 4000 });
      return () => (window as any).cancelIdleCallback?.(id);
    }
    const t = setTimeout(schedule, 2000);
    return () => clearTimeout(t);
  }, []);

  const { data } = useQuery({
    queryKey: ["public-locale"],
    enabled: idle,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("countries")
        .select("locale")
        .eq("is_default", true)
        .eq("is_active", true)
        .maybeSingle();
      if (error) return DEFAULT_LOCALE;
      return (data?.locale as string) || DEFAULT_LOCALE;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data) return;
    localStorage.setItem(LS_PUBLIC_LOCALE, data);
    setCached(data);
  }, [data]);

  return data || cached;
}


/**
 * Loads the current user's dashboard locale from `profiles.dashboard_locale`.
 * Falls back to localStorage for guests, then to DEFAULT_LOCALE.
 */
function useDashboardLocaleSync(userId: string | undefined) {
  const { data } = useQuery({
    queryKey: ["dashboard-locale", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("dashboard_locale")
        .eq("id", userId!)
        .maybeSingle();
      if (error) return null;
      return (data?.dashboard_locale as string) || null;
    },
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(LS_DASHBOARD_LOCALE) : null;
    const locale = data || stored || DEFAULT_LOCALE;
    setDashboardLocale(locale);
    if (data && typeof window !== "undefined") {
      localStorage.setItem(LS_DASHBOARD_LOCALE, data);
    }
  }, [data]);
}

/**
 * Provides both i18next instances and keeps <html lang> in sync with whichever
 * scope the user is currently viewing. Public scope drives lang for SEO when
 * visitors land on public pages; admin scope only updates lang while inside
 * /admin or /dashboard (these are noindex anyway, so no SEO impact).
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const publicLocale = usePublicLocaleSync();
  useDashboardLocaleSync(user?.id);

  const dashboard = isDashboardRoute(pathname);
  const activeI18n = useMemo(() => (dashboard ? dashboardI18n : publicI18n), [dashboard]);

  useEffect(() => {
    const locale = dashboard ? dashboardI18n.language : publicLocale;
    const meta = localeMeta(locale);
    document.documentElement.lang = meta.htmlLang;
  }, [dashboard, publicLocale]);

  return <I18nextProvider i18n={activeI18n}>{children}</I18nextProvider>;
}